/**
 * Index validation utilities for form validation
 * Provides validation functions for index creation and editing forms
 */

/**
 * Validates index name according to business rules
 * @param {string} name - The index name to validate
 * @param {Function} $t - Translation function for error messages
 * @param {Array} existingIndexes - Array of existing index names for uniqueness check
 * @param {boolean} isEdit - Whether this is an edit operation
 * @returns {Object} - Validation result with isValid boolean and error message
 */
export function validateIndexName(name, $t, existingIndexes = [], isEdit = false) {
  if (!name || name === '') {
    return {
      isValid: false,
      error: $t('indexes.validation.nameRequired')
    }
  }

  // Length validation - count spaces as normal characters
  if (name.length < 1) {
    return {
      isValid: false,
      error: $t('indexes.validation.nameTooShort')
    }
  }

  if (name.length > 30) {
    return {
      isValid: false,
      error: $t('indexes.validation.nameTooLong')
    }
  }

  // Check for whitespace
  if (/\s/.test(name)) {
    return {
      isValid: false,
      error: $t('indexes.validation.nameInvalidChars')
    }
  }

  // Check for uppercase characters
  if (/[A-Z]/.test(name)) {
    return {
      isValid: false,
      error: $t('indexes.validation.nameNoUppercase')
    }
  }

  // Character validation - allow lowercase alphanumeric, hyphen, underscore, and dot
  const nameRegex = /^[a-z0-9._-]+$/
  if (!nameRegex.test(name)) {
    return {
      isValid: false,
      error: $t('indexes.validation.nameInvalidChars')
    }
  }

  // Uniqueness validation (only for new indexes)
  if (!isEdit && existingIndexes.some(existing => existing.toLowerCase() === name.toLowerCase())) {
    return {
      isValid: false,
      error: $t('indexes.validation.nameExists')
    }
  }

  return {
    isValid: true,
    error: ''
  }
}

/**
 * Validates index description according to business rules
 * @param {string} description - The description to validate
 * @param {Function} $t - Translation function for error messages
 * @returns {Object} - Validation result with isValid boolean, error message, and character count
 */
export function validateIndexDescription(description, $t) {
  const maxLength = 4096
  const currentLength = description ? description.length : 0

  if (currentLength > maxLength) {
    return {
      isValid: false,
      error: $t('indexes.validation.descriptionTooLong', { max: maxLength }),
      characterCount: currentLength,
      maxLength
    }
  }

  return {
    isValid: true,
    error: '',
    characterCount: currentLength,
    maxLength
  }
}

/**
 * Validates and auto-corrects maximum size value
 * @param {string|number} value - The size value to validate
 * @param {string} unit - The unit (mb, gb, tb)
 * @param {Function} $t - Translation function for error messages
 * @returns {Object} - Validation result with isValid boolean, error message, and corrected value
 */
export function validateMaxSize(value, unit, $t) {
  // Extract numeric value from input (handle paste with non-numeric content)
  const numericValue = extractNumericValue(value)

  if (numericValue === null || numericValue === '') {
    return {
      isValid: false,
      error: $t('indexes.validation.maxSizeRequired'),
      correctedValue: ''
    }
  }

  const parsedValue = parseFloat(numericValue)

  if (isNaN(parsedValue)) {
    return {
      isValid: false,
      error: $t('indexes.validation.maxSizeInvalid'),
      correctedValue: ''
    }
  }

  // Range validation and auto-correction
  const minValue = 1
  const maxValue = 1024
  let correctedValue = parsedValue

  if (parsedValue < minValue) {
    correctedValue = minValue
  } else if (parsedValue > maxValue) {
    correctedValue = maxValue
  }

  return {
    isValid: true,
    error: '',
    correctedValue: correctedValue,
    wasAutoCorrected: correctedValue !== parsedValue
  }
}

/**
 * Validates and auto-corrects rotate time value
 * @param {string|number} value - The time value to validate
 * @param {Function} $t - Translation function for error messages
 * @returns {Object} - Validation result with isValid boolean, error message, and corrected value
 */
export function validateRotateTime(value, $t) {
  // Handle empty value - restore to default
  if (value === null || value === undefined || value === '') {
    return {
      isValid: true,
      error: '',
      correctedValue: 90, // Default value
      wasAutoCorrected: true
    }
  }

  // Extract numeric value from input (handle paste with non-numeric content)
  const numericValue = extractNumericValue(value)

  if (numericValue === null || numericValue === '') {
    return {
      isValid: true,
      error: '',
      correctedValue: 90, // Default value
      wasAutoCorrected: true
    }
  }

  const parsedValue = parseFloat(numericValue)

  if (isNaN(parsedValue)) {
    return {
      isValid: true,
      error: '',
      correctedValue: 90, // Default value
      wasAutoCorrected: true
    }
  }

  // Range validation and auto-correction
  const minValue = 2
  const maxValue = 365
  let correctedValue = parsedValue

  if (parsedValue < minValue) {
    correctedValue = minValue
  } else if (parsedValue > maxValue) {
    correctedValue = maxValue
  }

  return {
    isValid: true,
    error: '',
    correctedValue: correctedValue,
    wasAutoCorrected: correctedValue !== parsedValue
  }
}

/**
 * Extracts numeric characters from a string, handling paste operations
 * @param {string|number} input - The input value
 * @returns {string|null} - The extracted numeric value or null if no numbers found
 */
export function extractNumericValue(input) {
  if (typeof input === 'number') {
    return input.toString()
  }

  if (typeof input !== 'string') {
    return null
  }

  // Extract only numeric characters and decimal point
  const numericChars = input.replace(/[^0-9.]/g, '')

  // Handle multiple decimal points - keep only the first one
  const parts = numericChars.split('.')
  if (parts.length > 2) {
    return parts[0] + '.' + parts.slice(1).join('')
  }

  return numericChars || null
}

/**
 * Validates all index form fields
 * @param {Object} formData - The form data object
 * @param {Function} $t - Translation function for error messages
 * @param {Array} existingIndexes - Array of existing index names
 * @param {boolean} isEdit - Whether this is an edit operation
 * @returns {Object} - Validation result with overall validity and field-specific errors
 */
export function validateIndexForm(formData, $t, existingIndexes = [], isEdit = false) {
  const nameValidation = validateIndexName(formData.name, $t, existingIndexes, isEdit)
  const descriptionValidation = validateIndexDescription(formData.description, $t)
  const maxSizeValidation = validateMaxSize(formData.max_size, formData.unit, $t)
  const rotateTimeValidation = validateRotateTime(formData.rotate_time, $t)

  const isValid = nameValidation.isValid &&
    descriptionValidation.isValid &&
    maxSizeValidation.isValid &&
    rotateTimeValidation.isValid

  return {
    isValid,
    errors: {
      name: nameValidation.error,
      description: descriptionValidation.error,
      max_size: maxSizeValidation.error,
      rotate_time: rotateTimeValidation.error
    },
    corrections: {
      max_size: maxSizeValidation.correctedValue,
      rotate_time: rotateTimeValidation.correctedValue
    },
    autoCorrected: {
      max_size: maxSizeValidation.wasAutoCorrected,
      rotate_time: rotateTimeValidation.wasAutoCorrected
    },
    characterCount: descriptionValidation.characterCount,
    maxDescriptionLength: descriptionValidation.maxLength
  }
}
